import { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { ECSegmentList } from './ECSegmentList/ECSegmentList';
import { SelectOptions } from 'components/commonType';
import { AdvertiserManager, DefaultAdvertiserManager } from 'core/advertiser/AdvertiserManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ECSegmentForm } from './ECSegmentForm/ECSegmentForm';
import { useCreateECSegmentFormModel, useEditECSegmentFormModel } from './ECSegmentForm/ECSegmentFormModel';
import { partial } from 'lodash';
import RouteWithPermission from 'components/RouteWithPermission/RouteWithPermission';
import { addOnEnabled, hasFuncs } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { ROUTE_PATH } from 'enum/RoutePath';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { ADDONFEATURE } from 'core/agency/AddonFeature';

const advertiserManager: AdvertiserManager = new DefaultAdvertiserManager();
export const ECSegments: React.FC = () => {

  const [advertiserOptions, setAdvertiserOptions] = useState<SelectOptions[]>([]);

  const {
    loading,
    callAPIs
  } = useCallAPI();

  useEffect(() => {
    callAPIs(
      [() => advertiserManager.getAdvertiserOptions()],
      (advertiserOptions: SelectOptions[]) => {
        setAdvertiserOptions(advertiserOptions);
      }
    );
  }, [callAPIs]);

  const createSegmentPageModel = useMemo(
    () => partial(useCreateECSegmentFormModel, advertiserOptions),
    [advertiserOptions]
  );

  const editSegmentPageModel = useMemo(
    () => partial(useEditECSegmentFormModel, advertiserOptions),
    [advertiserOptions]
  );

  const renderCreateSegmentPage = useCallback(() => {
    return (
      <>
        {loading && <LoadingIndicator />}
        <ECSegmentForm useModel={createSegmentPageModel} />
      </>
    );
  }, [loading, createSegmentPageModel]);

  const renderEditSegmentPage = useCallback((props) => {
    return (
      <>
        {loading && <LoadingIndicator />}
        <PermissionChecker
          permissionAware={hasFuncs(Permission.EC_SEGMENT_WRITE).and(
            addOnEnabled(ADDONFEATURE.COMPANY.SEGMENT_MANAGEMENT)
          )}
          redirectTo={`${props.match.url}/${ROUTE_PATH.ERROR403}`}
        >
          <ECSegmentForm useModel={editSegmentPageModel} />
        </PermissionChecker>
      </>
    );
  }, [loading, editSegmentPageModel]);

  const renderSegmentList = useCallback(() => {
    return (
      <>
        {loading && <LoadingIndicator />}
        <ECSegmentList
          advertiserOptions={advertiserOptions}
        />
      </>
    );
  }, [loading, advertiserOptions]);
  return (
    <Switch>
      <RouteWithPermission
        path={'/ec-segments/new'}
        permissionAware={hasFuncs(Permission.EC_SEGMENT_WRITE).and(
          addOnEnabled(ADDONFEATURE.COMPANY.SEGMENT_MANAGEMENT)
        )}
        redirectPath={`/ec-segments/new/${ROUTE_PATH.ERROR403}`}
        render={renderCreateSegmentPage}
      />
      <Route
        path={'/ec-segments/:segmentId/edit'}
        render={renderEditSegmentPage}
      />
      <Route render={renderSegmentList} />
    </Switch>
  );
};
